"use client";

import { useRef } from "react";
import { useGSAP } from "@gsap/react";
import { gsap } from "gsap";
import Image from "next/image";
import { cn } from "@/app/utils/className";
import { useRouter } from "next/navigation";
import { useSectionNavTheme } from "@/app/hooks/useSectionNavTheme";
import { caseStudiesGridCards, BaseSolutionCard } from "./case-studies-data";

interface SolutionCardProps {
  solution: BaseSolutionCard;
  bgColour: string;
  className?: string;
}

interface StatsProps {
  solution: BaseSolutionCard;
  className?: string;
  bgColour: string;
}

const cardColors = ["bg-haizea-blue", "bg-haizea-green", "bg-haizea-orange"];

const CaseStudyCard = ({
  solution,
  bgColour,
  className,
}: SolutionCardProps) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const router = useRouter();

  useGSAP(() => {
    const card = cardRef.current;
    if (!card) return;

    // Set initial state
    gsap.set(card, {
      opacity: 0,
      y: 80,
      scale: 0.95,
    });

    // Animate on scroll
    gsap.to(card, {
      opacity: 1,
      y: 0,
      scale: 1,
      duration: 0.8,
      ease: "power3.out",
      scrollTrigger: {
        trigger: card,
        start: "top 90%",
      },
    });
  }, []);

  const handleClick = () => {
    if (solution.link) {
      router.push(solution.link);
    }
  };

  // Extract gradient color based on bgColour prop
  const getGradient = () => {
    if (bgColour.includes("haizea-blue"))
      return "from-haizea-blue/10 to-haizea-blue";
    if (bgColour.includes("haizea-green"))
      return "from-haizea-green/10 to-haizea-green";
    if (bgColour.includes("haizea-orange"))
      return "from-haizea-orange/10 to-haizea-orange";
    return "from-white/10 to-white/5";
  };

  return (
    <div
      ref={cardRef}
      className={cn(
        "group relative overflow-hidden rounded-2xl cursor-pointer transition-all duration-500 hover:scale-[1.01] w-full shadow-lg hover:shadow-2xl",
        getGradient(),
        className
      )}
      onClick={handleClick}
    >
      {/* Background & Card Structure */}
      <div className="relative h-full bg-white/5 backdrop-blur-sm border border-white/10 hover:bg-white/10 rounded-2xl hover:border-white/20 transition-all duration-500 min-h-[400px] overflow-hidden">
        {/* Gradient glow effect on hover */}
        <div
          className={`absolute inset-0 bg-gradient-to-br ${getGradient()} opacity-30 group-hover:opacity-50 transition-opacity duration-500 blur-xl`}
        ></div>

        {/* Content */}
        <div className="relative z-10 h-full flex flex-col lg:flex-row text-white">
          {/* Image Section */}
          <div className="relative w-full lg:w-[300px] xl:w-[500px] h-[300px] lg:h-auto overflow-hidden">
            <Image
              src={solution.image}
              alt={solution.title}
              className="object-cover w-full h-full transition-transform duration-700 group-hover:scale-110"
              width={500}
              height={400}
            />
            {/* Image overlay gradient */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-gray-900/50 lg:to-gray-900/80"></div>
          </div>

          {/* Content Section */}
          <div
            className={cn("flex-1 p-8 xl:p-12 flex flex-col justify-between")}
          >
            {/* Header */}
            <div>
              <div className="mb-4 lg:mb-6">
                <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-2 transition-colors duration-300">
                  {solution.title}
                </h3>

                <p className="text-lg md:text-xl text-slate-100">
                  {solution.subtitle}
                </p>
              </div>

              <p className="text-base md:text-lg leading-relaxed mb-6 lg:mb-8 text-slate-100 max-w-2xl">
                {solution.description}
              </p>
            </div>

            {/* Stats */}
            {solution.stats && (
              <div className="mb-6 lg:mb-8">
                <Stats solution={solution} bgColour={bgColour} />
              </div>
            )}

            {/* CTA */}
            <div>
              <div className="group/btn inline-flex items-center text-slate-100 border border-white rounded-full px-6 py-3 hover:bg-slate-200 hover:text-slate-700 transition-all duration-300 text-base font-medium shadow-lg hover:shadow-xl">
                Learn More
                <Image
                  src="/images/arrow-right.svg"
                  alt="Arrow Right"
                  width={20}
                  height={20}
                  className="ml-2 invert group-hover/btn:invert-0 animate-[movehorizontal_1.5s_infinite_alternate] h-auto"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Hover accent line - matching other sections */}
        <div className="absolute z-20 bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-white/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

        {/* Corner accents - matching other sections */}
        <div className="absolute z-20 top-4 right-4 w-8 h-8 border-t-2 border-r-2 border-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
        <div className="absolute z-20 bottom-4 left-4 w-8 h-8 border-b-2 border-l-2 border-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
      </div>
    </div>
  );
};

export const CaseStudiesGrid = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);

  useSectionNavTheme({
    trigger: `.case-studies-grid`,
    isDark: true,
  });

  useGSAP(() => {
    const section = sectionRef.current;
    const title = titleRef.current;

    if (!section || !title) return;

    // Animate title on scroll
    gsap.fromTo(
      title,
      {
        opacity: 0,
        y: 40,
      },
      {
        opacity: 1,
        y: 0,
        duration: 1,
        ease: "power2.out",
        scrollTrigger: {
          trigger: title,
          start: "top 90%",
          toggleActions: "play none none reverse",
        },
      }
    );
  }, []);

  return (
    <section
      ref={sectionRef}
      className="case-studies-grid relative z-10 -mt-[1px] bg-white dark:bg-gradient-to-b from-haizea-dark to-slate-800"
    >
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Gradient Orbs */}
        <div className="absolute top-20 -left-40 w-96 h-96 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 rounded-full blur-3xl animate-pulse" />
        <div
          className="absolute bottom-20 -right-40 w-96 h-96 bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-full blur-3xl animate-pulse"
          style={{ animationDelay: "2s" }}
        />
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-72 h-72 bg-gradient-to-r from-purple-500/15 to-pink-500/15 rounded-full blur-3xl animate-pulse"
          style={{ animationDelay: "4s" }}
        />
      </div>

      {/* Content Overlay */}
      <div className="container relative z-10">
        {/* Case Studies Grid */}
        <div className="space-y-8 md:space-y-12 lg:space-y-16">
          {caseStudiesGridCards.map((solution, index) => (
            <CaseStudyCard
              key={solution.id}
              solution={solution}
              bgColour={cardColors[index % cardColors.length]}
              className={cn("relative -top-56")}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

const Stats = ({ solution, className, bgColour }: StatsProps) => {
  // Extract gradient based on the solution's bgColor
  const getGradient = () => {
    if (bgColour.includes("bg-haizea-blue"))
      return "from-haizea-blue/20 to-white/10 group-hover:to-white/30";
    if (bgColour.includes("bg-haizea-green"))
      return "from-haizea-green/20 to-white/10 group-hover:to-white/30";
    if (bgColour.includes("bg-haizea-orange"))
      return "from-haizea-orange/20 to-white/10 group-hover:to-white/30";
    return "from-white/10 to-white/5";
  };

  const gradient = getGradient();

  return (
    <div className={cn("grid grid-cols-3 gap-3 md:gap-4", className)}>
      {solution.stats?.map((stat, idx) => (
        <div
          key={idx}
          className={`bg-gradient-to-br ${gradient} backdrop-blur-sm border border-white/10 rounded-xl p-3 md:p-4 text-center hover:bg-white/10 transition-all duration-300`}
        >
          <div className="text-xl md:text-2xl lg:text-3xl font-bold mb-1 md:mb-2 text-white">
            {stat.value}
          </div>
          <div className="text-xs md:text-sm font-medium text-slate-100">
            {stat.label}
          </div>
        </div>
      ))}
    </div>
  );
};
